// Core Modules
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';

// Tokens
import { WINDOW } from '@core/token/tokens';

// Services
import { AuthService } from '@core/services/auth/auth.service';

// Layout Components
import { HeaderComponent } from '@layout/header/header.component';
import { SideNavComponent } from '@layout/side-nav/side-nav.component';
import { FooterComponent } from '@layout/footer/footer.component';

// Shared Components
import { ToastComponent } from '@shared/components/toast/toast.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, SideNavComponent, FooterComponent, RouterOutlet, ToastComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  // Injectors
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WINDOW);

  /** Whether the off-canvas nav drawer is open (small screens only). */
  public readonly mobileNavOpen = signal(false);

  /** Bootstrap `md` and up — the point at which the nav returns to an inline rail. */
  private readonly desktopQuery = this.window.matchMedia('(min-width: 768px)');

  constructor() {
    // Close the drawer when the viewport grows back to the inline-rail layout.
    const onDesktopChange = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        this.mobileNavOpen.set(false);
      }
    };
    this.desktopQuery.addEventListener('change', onDesktopChange);
    this.destroyRef.onDestroy(() => this.desktopQuery.removeEventListener('change', onDesktopChange));
  }

  /** Toggle the mobile nav drawer (triggered by the header hamburger). */
  public toggleMobileNav(): void {
    this.mobileNavOpen.update((open) => !open);
  }

  /** Close the mobile nav drawer (backdrop tap or nav selection). */
  public closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  /** True when the URL is a bare (chrome-less) route: login or the empty root. Ignores query/hash. */
  private isBareRoute(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return [`/${MainNavigationEnum.LOGIN}`, '/'].includes(path);
  }

  /**
   * Hide the header/footer chrome on login/empty routes. Asymmetric timing keeps each transition to
   * a single outlet activation: seed from the current URL, switch to the chrome shell eagerly
   * (NavigationStart into the app), but settle to the bare shell only at NavigationEnd.
   */
  private readonly hideNavigation = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationStart | NavigationEnd => (event instanceof NavigationStart && !this.isBareRoute(event.url)) || event instanceof NavigationEnd),
      map((event) => (event instanceof NavigationEnd ? this.isBareRoute(event.urlAfterRedirects) : false))
    ),
    { initialValue: this.isBareRoute(this.window.location.pathname) }
  );

  // AuthService observables (frozen) consumed read-only as signals.
  private readonly isAuthInProgress = toSignal(this.auth.isAuthenticationInProgress$, { initialValue: false });
  private readonly isClientNavInProgress = toSignal(this.auth.isClientNavigationInProgress$, { initialValue: false });

  /** Combined shell state consumed by the template. */
  public readonly appState = computed(() => {
    const hideNavigation = this.hideNavigation();
    return {
      isAuthInProgress: this.isAuthInProgress(),
      hideNavigation,
      showHeader: !hideNavigation,
      showContent: !this.isClientNavInProgress(),
      showFooter: !hideNavigation,
    };
  });
}
